// src/components/Common/MoodleEventBooking/CreateEventLauncher.tsx
import React, { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { useSelector } from 'react-redux';
import { HbrForm, HbrOption, HbrSelect } from '@harbor/elements-react-standalone';
import { RootState } from '../../../support/store';
import { MoodleCourse } from '../../../types/moodleEventBooking';
import { CreateEventModal } from './CreateEventModal';
import { filterCoursesByCategoryId } from '../../../hooks/useMoodleHooks';
import StbApiModal from '../../security-toolbox-ui/StbApiModal';
import { selectMoodleCategoriesMap, selectMoodleCoursesMap } from '../../../support/slices/moodleSlice';


type CreateEventLauncherProps = {
  mode: 'create' | 'edit';
  open: boolean;
  courseId?: number | string;
  eventId?: number | string;
  onClose: () => void;
  onCreated?: (saved?: any) => void;
  customerId?: string;
  opportunityId?: string;
};

function asArray<T = any>(obj: Record<number, T> | undefined): T[] {
  return obj ? Object.values(obj) : [];
}

export function CreateEventLauncher({
  mode,
  open,
  courseId,
  eventId,
  onClose,
  onCreated,
  customerId,
  opportunityId,
}: CreateEventLauncherProps) {
  const coursesMap = useSelector(selectMoodleCoursesMap);
  const categoriesMap = useSelector(selectMoodleCategoriesMap);

  const saveCallbackRef = React.useRef<() => Promise<void>>();

  const allCourses = useMemo<MoodleCourse[]>(
    () => asArray<MoodleCourse>(coursesMap).filter((c) => !!c.fullname),
    [coursesMap],
  );
  const allCategories = useMemo(
    () => asArray<any>(categoriesMap),
    [categoriesMap]
  );

  const filteredCourses = useMemo<MoodleCourse[]>(
    () => filterCoursesByCategoryId(allCourses ?? [], allCategories ?? [], 58) ?? [],
    [allCourses, allCategories],
  );

  const [selectCourseOpen, setSelectCourseOpen] = useState<boolean>((!courseId || String(courseId) === '') && mode === 'create');
  useEffect(() => {
    setSelectCourseOpen(open && (!courseId || String(courseId) === '') && mode === 'create');
  }, [open, courseId, mode]);

  const [selectedCourseId, setSelectedCourseId] = useState<string>(courseId ? String(courseId) : String(filteredCourses[0]?.id));
  const selectedCourse = useMemo<MoodleCourse | undefined>(() => {
    if (!selectedCourseId || selectedCourseId === '' || filteredCourses.length === 0) return undefined;
    return filteredCourses.find((c) => String(c.id) === String(selectedCourseId));
  }, [selectedCourseId, filteredCourses]);

  const [submitDisabled, setSubmitDisabled] = useState<boolean>(true);
  useEffect(() => {
    if (selectCourseOpen) {
      setSubmitDisabled(!selectedCourseId || selectedCourseId === '');
    }
  }, [selectCourseOpen, selectedCourseId]);

  // Handlers for the "Select Course" modal (create mode).
  const handleCourseSelectChange = (e: any) => {
    const value = e.detail?.value ?? e.target?.value ?? '';
    setSelectedCourseId(String(value || ''));
  };

  const handleCreateFromSelection = async () => {
    if (selectCourseOpen) {
      if (!selectedCourseId) return;
      setSelectCourseOpen(false);
    } else if (saveCallbackRef.current) {
      await saveCallbackRef.current();
    }
  };

  const handleClose = async () => {
    setSelectedCourseId(String(filteredCourses[0]?.id));
    onClose();
  };

  if (mode === 'edit' && (!courseId || !eventId)) {
    return null;
  }

  return (
    <StbApiModal
      title={eventId ? 'Update Event' : 'Create Event'}
      role={eventId ? "update" : "save"}
      open={open}
      size="large"
      onSubmit={handleCreateFromSelection}
      submitDisabled={submitDisabled}
      submitLabel={eventId ? 'Update Event' : 'Create Event'}
      suppressCloseOnSubmit={selectCourseOpen}
      onClose={handleClose}
      scrollable={!selectCourseOpen}
      suppressResponses={selectCourseOpen}
      submitSuccess="Event saved successfully"
      submitError="There was an error saving the event"
    >
      {selectCourseOpen 
        ? (
            <HbrForm>
              <HbrSelect
                name="courseId"
                label="Available Courses"
                value={selectedCourseId}
                onHbr-change={handleCourseSelectChange}
                required
                placeholder="Select a course"
              >
                {filteredCourses.map((c) => (
                  <HbrOption key={String(c.id)} value={String(c.id)}>
                    {c.displayname || c.fullname || `Course #${c.id}`}
                  </HbrOption>
                ))}
              </HbrSelect>

              {selectedCourse?.courseimage && (
                <div style={{ marginBottom: 12 }}>
                  <img
                    src={selectedCourse.courseimage}
                    alt={selectedCourse.fullname || 'Course image'}
                    style={{ maxWidth: '100%', borderRadius: 8 }}
                  />
                </div>
              )}

              {selectedCourse?.summary && (
                <div
                  style={{
                    border: '1px solid #e2e2e2',
                    borderRadius: 8,
                    padding: 12,
                    background: '#fafbfc',
                    marginBottom: 12,
                  }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedCourse.summary) }}
                />
              )}
            </HbrForm>
          )
        : (
            <CreateEventModal
              eventId={mode === 'edit' ? eventId : undefined}
              onCreated={async (saved) => {
                onCreated?.(saved);
              }}
              courseId={mode === 'edit' ? courseId : selectedCourseId}
              customerId={customerId}
              opportunityId={opportunityId}
              onSaveRegister={(saveFn) => {
                saveCallbackRef.current = saveFn;
              }}
              setSubmitDisabled={setSubmitDisabled}
            />
          )
      }
    </StbApiModal>
  );
}